import { useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { saveUrl } from "@/pages/UserHub/Inbox/Views/components/exportFile";
import { InboxViewService } from "@/services/inboxView/InboxViewService";

interface ReportJobEvent {
  request_id: string;
  view_id: string;
  kind: "pdf_download" | "test_report";
  status: "completed" | "sent" | "skipped_no_rows" | "failed";
  export_file?: string;
}

interface PendingReportJob {
  toastId: string | number;
  /** Shown when a test report is sent, e.g. "olga@example.com" or "3 recipients". */
  recipientsLabel?: string;
  timeout: number;
}

// How long a job may stay silent before the page stops waiting for it.
const JOB_TIMEOUT_MS = 4 * 60 * 1000;

// Jobs this browser tab started, by request id. The channel carries every
// customer's events, so anything not listed here is ignored.
const pendingJobs = new Map<string, PendingReportJob>();

const takeJob = (requestId: string): PendingReportJob | null => {
  const job = pendingJobs.get(requestId);
  if (!job) return null;
  window.clearTimeout(job.timeout);
  pendingJobs.delete(requestId);
  return job;
};

/** Registers a PDF download or test send that was just started, with its loading toast. */
export function trackReportJob(requestId: string, toastId: string | number, recipientsLabel?: string): void {
  const timeout = window.setTimeout(() => {
    failReportJob(requestId, "This is taking longer than expected. Please try again.");
  }, JOB_TIMEOUT_MS);
  pendingJobs.set(requestId, { toastId, recipientsLabel, timeout });
}

export function failReportJob(requestId: string, message: string): void {
  const job = takeJob(requestId);
  if (job) toast.error(message, { id: job.toastId });
}

async function finishPdfDownload(event: ReportJobEvent, job: PendingReportJob) {
  if (event.status !== "completed" || !event.export_file) {
    toast.error("We couldn't build the PDF report. Please try again.", { id: job.toastId });
    return;
  }
  try {
    const { url, filename } = await InboxViewService.exportPdfLink({ viewId: event.view_id, file: event.export_file });
    saveUrl(url, filename);
    toast.success("Your PDF report is ready.", {
      id: job.toastId,
      action: { label: "Download", onClick: () => saveUrl(url, filename) },
    });
  } catch {
    toast.error("Your PDF report was built but could not be downloaded. Please try again.", { id: job.toastId });
  }
}

function finishTestReport(event: ReportJobEvent, job: PendingReportJob) {
  if (event.status === "sent") {
    toast.success(`Test report sent to ${job.recipientsLabel ?? "your recipients"}.`, { id: job.toastId });
  } else if (event.status === "skipped_no_rows") {
    toast.info("No data in the report period yet — nothing to send.", { id: job.toastId });
  } else {
    toast.error("Test report failed to send.", { id: job.toastId });
  }
}

/** Mounted once for the app: finishes report jobs when the server announces them. */
export const useInboxViewReportJobs = () => {
  useEffect(() => {
    const channel = supabase
      .channel("inbox-view-updates")
      .on("broadcast", { event: "report_job_finished" }, ({ payload }: { payload: ReportJobEvent }) => {
        const job = takeJob(payload.request_id);
        if (!job) return;
        if (payload.kind === "pdf_download") void finishPdfDownload(payload, job);
        else finishTestReport(payload, job);
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);
};
